import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';

import './styles/floating-navbar.css';
import './styles/footer.css';
import Navbar from './components/ui/Navbar';
import Footer from './components/ui/Footer';
import Preloader from './components/Preloader';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductList from './pages/ProductList';
import ProductDetail from './pages/ProductDetail';
import BlogDetail from './pages/BlogDetail';
import AdminPanel from './pages/AdminPanel';
import { getProduct, getProducts, getCategories } from './lib/api';

// Global error handler for production
if (process.env.NODE_ENV === 'production') {
  window.addEventListener('error', (event) => {
    // Prevent error from displaying to user
    event.preventDefault();
    // Silently log for debugging if needed
    return true;
  });

  window.addEventListener('unhandledrejection', (event) => {
    // Prevent unhandled promise rejections from displaying
    event.preventDefault();
    return true;
  });
}

function AppContent() {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Prevent automatic scroll restoration by the browser (helps mobile)
  React.useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      try { window.history.scrollRestoration = 'manual'; } catch (e) { }
    }
  }, []);

  // Components are now lazy loaded on-demand for better initial performance
  const preloadComponents = [];

  const handleCategoryClick = (category) => {
    navigate(`/products/${category._id}`);
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product._id}`);
  };

  const handleViewProducts = () => {
    navigate('/products');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const isAdminRoute = location.pathname === '/admin-panel-7f8e9d2a1b3c';
  return (
    <>
      {isLoading && (
        <Preloader
          onLoadComplete={() => {
            try {
              window.dispatchEvent(new CustomEvent('sitcorp:heroAutoReverse', { detail: { duration: 1800 } }));
            } catch (err) {
              // ignore if CustomEvent not supported
            }
            setTimeout(() => {
              setIsLoading(false);
              try {
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
              } catch (e) {
                // fallback
                window.scrollTo(0, 0);
              }
              document.body.scrollTop = 0;
              document.documentElement.scrollTop = 0;
            }, 80);
          }}
          preloadComponents={preloadComponents}
        />
      )}
      <div className="App site-root" style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
        {!isAdminRoute && <Navbar />}
        <main className="site-main">
          <Routes>
            <Route path="/" element={
              <Home
                onCategoryClick={handleCategoryClick}
                onViewProducts={handleViewProducts}
                onProductClick={handleProductClick}
              />
            } />
            <Route path="/products" element={
              <Products
                onCategoryClick={handleCategoryClick}
                onProductClick={handleProductClick}
              />
            } />
            <Route path="/products/:categoryId" element={<ProductListWrapper onProductClick={handleProductClick} />} />
            <Route path="/product/:productId" element={<ProductDetailWrapper />} />
            <Route path="/blog/:blogId" element={<BlogDetailWrapper />} />
            <Route path="/admin-panel-7f8e9d2a1b3c" element={<AdminPanel />} />
          </Routes>
        </main>
        {!isAdminRoute && <Footer />}
      </div>
    </>
  );
}

// Wrapper components to handle URL params
function ProductListWrapper({ onProductClick }) {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // Fetch all categories and find the one with matching ID
    const fetchCategory = async () => {
      try {
        const categories = await getCategories();
        const foundCategory = categories.find(cat => cat._id === categoryId);
        if (foundCategory) {
          // Fetch products for this category
          const products = await getProducts(categoryId);
          setCategory({ ...foundCategory, products });
        }
        setLoading(false);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Failed to fetch category:', err);
        }
        setLoading(false);
      }
    };

    fetchCategory();
  }, [categoryId]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#FAF9F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid rgba(0, 0, 0, 0.1)', borderTopColor: '#1A1A1A', borderRadius: '999px', margin: '0 auto 1.5rem', animation: 'spin 0.9s linear infinite' }}></div>
          <p style={{ color: '#1A1A1A', fontSize: '1rem' }}>Loading category...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!category || !category.products || category.products.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: '#FAF9F6', padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <h1 style={{ fontSize: '2.5rem', margin: '0 0 1rem', color: '#1A1A1A' }}>Category Not Found</h1>
          <p style={{ margin: '0 0 2rem', color: '#4b5563', fontSize: '1.05rem' }}>
            No products found in this category.
          </p>
          <button 
            onClick={() => navigate('/products')}
            style={{ background: '#1A1A1A', color: '#fff', padding: '0.85rem 1.5rem', border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer' }}
          >
            View All Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <ProductList
      category={category}
      onProductClick={onProductClick}
      onBack={() => navigate('/products')}
    />
  );
}

function ProductDetailWrapper() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    // Fetch product by ID using the proper API function
    const fetchProduct = async () => {
      try {
        const data = await getProduct(productId);
        if (!data) {
          setError('Product not found');
        } else {
          setProduct(data);
        }
        setLoading(false);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Failed to fetch product:', err);
        }
        setError(err.message || 'Failed to load product');
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#FAF9F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid rgba(0, 0, 0, 0.1)', borderTopColor: '#1A1A1A', borderRadius: '999px', margin: '0 auto 1.5rem', animation: 'spin 0.9s linear infinite' }}></div>
          <p style={{ color: '#1A1A1A', fontSize: '1rem' }}>Loading product details...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div style={{ minHeight: '100vh', background: '#FAF9F6', padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <h1 style={{ fontSize: '2.5rem', margin: '0 0 1rem', color: '#1A1A1A' }}>Product Not Found</h1>
          <p style={{ margin: '0 0 2rem', color: '#4b5563', fontSize: '1.05rem' }}>
            {error || 'The product you\'re looking for is not available.'}
          </p>
          <button 
            onClick={() => navigate('/products')}
            style={{ background: '#1A1A1A', color: '#fff', padding: '0.85rem 1.5rem', border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer' }}
          >
            View All Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <ProductDetail
      product={product}
      onBack={() => navigate(-1)}
    />
  );
}

function BlogDetailWrapper() {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const blog = location.state?.blog;

  // Force component remount on blog change to ensure CSS is applied
  React.useEffect(() => {
    // Trigger a re-render by scrolling to top
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [blogId]);

  if (!blog) {
    // If no blog in state, could fetch from API or redirect
    return <div>Blog not found</div>;
  }

  return (
    <BlogDetail
      key={blogId}
      blog={blog}
      onBack={() => navigate('/')}
    />
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;
